import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Avatar,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Popover
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { useAtom } from 'jotai';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { isLayoutMobileAtom } from '@/store/layout.ts';

import QuickSearch from '../QuickSearch/QuickSearch.tsx';
import useStyles from './SearchableSelect.styles.ts';
import type { SearchableSelectProps } from './SearchableSelect.types.ts';
import { optionLabelFormatter } from './SearchableSelect.utils.ts';
import SearchableSelectButton from './SearchableSelectButton.tsx';

const SearchableSelect = ({
  className,
  CustomAvatar,
  field,
  filterOptions,
  hasAvatars = false,
  multiselect = true,
  searchable = false,
  selectedOptions = [],
  showCounts = true,
  open = false,
  title,
  titleButton,
  titleButtonProps,
  variant = 'dropdown',
  onApply,
  onCancel,
  onChange,
  onSelect
}: SearchableSelectProps) => {
  const [isLayoutMobile] = useAtom<boolean>(isLayoutMobileAtom);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [search, setSearch] = useState<string>('');
  // local draft selection, valid only for the parent selection it was based on
  const [draft, setDraft] = useState<{
    base: string[];
    value: string[];
  } | null>(null);
  const isDraftCurrent = draft !== null && isEqual(draft.base, selectedOptions);
  if (draft !== null && !isDraftCurrent) {
    // parent selection changed: drop the stale draft so it cannot resurface later
    setDraft(null);
  }
  const selectedFilterOptions =
    draft !== null && isDraftCurrent ? draft.value : selectedOptions;

  const setSelectedFilterOptions = (value: string[]) =>
    setDraft({ base: selectedOptions, value });
  const { classes } = useStyles();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const isOpen = Boolean(anchorEl);

  useEffect(() => {
    if (open && buttonRef.current && !anchorEl) {
      setAnchorEl(buttonRef.current);
    }
  }, [buttonRef.current]);

  //-----------------------------------------------------------------
  const handleCancel = () => {
    setAnchorEl(null);
    onCancel?.();
  };

  const handleMultiSelect = () => {
    if (onApply) onApply(field, [...selectedFilterOptions]);
    setAnchorEl(null);
  };

  const handleOptionToggle = (key: string) => {
    const options = selectedFilterOptions.includes(key)
      ? selectedFilterOptions.filter((item) => item !== key)
      : [...selectedFilterOptions, key];

    setSelectedFilterOptions(options);
    if (variant === 'buttongroup') {
      if (key.includes('Null') && onApply) {
        const newValue = selectedFilterOptions.includes(key) ? [] : [key];
        onApply(field, newValue);
        return;
      }
      if (onApply) onApply(field, [...options]);
    }
    if (variant === 'toggle') {
      let value: string[] = [];
      if (!selectedOptions.includes('true')) {
        value = ['true'];
      }
      if (onApply) onApply(field, value);
    }
  };

  const handleSelect = (value: string | number) => {
    // multiselect
    if (multiselect && typeof value === 'string') {
      if (onChange) {
        onChange(value, selectedFilterOptions);
        return;
      }
      handleOptionToggle(value);
      return;
    }
    // non-multiselect
    if (typeof value === 'number' || typeof value === 'string') {
      if (onSelect) onSelect(value);
    }
    setAnchorEl(null);
  };

  //-----------------------------------------------------------------
  const togglePopper = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setSearch('');
      setAnchorEl(event.currentTarget);
      setDraft(null);
    },
    [selectedOptions]
  );

  const filteredOptions = useMemo(
    () =>
      search === ''
        ? filterOptions
        : filterOptions.filter((option) =>
            option.label.toLowerCase().includes(search.toLowerCase())
          ),
    [filterOptions, search]
  );

  const titleBtn = useMemo(
    () => (
      <div {...(isLayoutMobile && { style: { width: '100%' } })}>
        {titleButton === 'button' && titleButtonProps !== undefined && (
          <Button {...titleButtonProps} onClick={togglePopper} ref={buttonRef}>
            {titleButtonProps.label}
          </Button>
        )}
        {titleButton === 'split-button' && titleButtonProps !== undefined && (
          <ButtonGroup
            variant="contained"
            {...(isLayoutMobile && { sx: { width: '100%' } })}
          >
            <Button {...titleButtonProps} onClick={togglePopper}>
              {titleButtonProps.label}
            </Button>
            <Button size="small" onClick={togglePopper}>
              <FontAwesomeIcon icon={faCaretDown} size="1x" />
            </Button>
          </ButtonGroup>
        )}
        {titleButton !== 'button' && titleButton !== 'split-button' && (
          <SearchableSelectButton
            title={title}
            buttonLabel={titleButtonProps?.label}
            selectedFilterOptions={selectedOptions}
            filterOptions={filterOptions}
            onClick={togglePopper}
            ref={buttonRef}
          />
        )}
      </div>
    ),
    [
      filterOptions,
      selectedOptions,
      title,
      titleButton,
      titleButtonProps,
      togglePopper
    ]
  );

  const buttonGroup = (
    <ButtonGroup variant="outlined" className={classes.buttonGroup}>
      {filteredOptions.map((option) => (
        <Button
          key={String(option.id ?? option.value)}
          variant={
            selectedOptions.includes(option.value as string)
              ? 'contained'
              : 'outlined'
          }
          color="secondary"
          onClick={() => handleOptionToggle(option.value as string)}
        >
          {option.label}
          {showCounts && (
            <Chip label={option.count || 0} size="small" sx={{ ml: 0.5 }} />
          )}
        </Button>
      ))}
    </ButtonGroup>
  );

  const subHeader = (
    <ListSubheader
      component="div"
      className={classes.searchableSelectListSubheader}
    >
      {title} Filter
      {selectedFilterOptions.length > 0 && (
        <Button variant="text" onClick={() => setSelectedFilterOptions([])}>
          Clear
        </Button>
      )}
    </ListSubheader>
  );

  const dropdown = (
    <Box
      {...(isLayoutMobile &&
        titleButton === 'split-button' && { sx: { width: '100%' } })}
    >
      {titleBtn}
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        <List
          disablePadding
          className={classes.searchableSelectList}
          subheader={multiselect ? subHeader : null}
        >
          {searchable && (
            <ListItem className={classes.searchableSelectSearch}>
              <QuickSearch onChange={setSearch} />
            </ListItem>
          )}
          {filteredOptions.length === 0 && (
            <ListItem>
              <i>No matches found</i>
            </ListItem>
          )}
          <ListItem
            sx={{ maxHeight: '50vh', overflow: 'auto', p: 0, display: 'block' }}
          >
            <List>
              {filteredOptions.map((option) => (
                <ListItem
                  className={showCounts ? 'with-secondary' : ''}
                  key={String(option.id ?? option.value)}
                  disablePadding
                  secondaryAction={
                    showCounts && (
                      <Chip label={option.count || 0} size="small" />
                    )
                  }
                >
                  <ListItemButton
                    disableRipple
                    role={undefined}
                    onClick={() => {
                      handleSelect(option.value);
                    }}
                    sx={{ py: hasAvatars ? 0.25 : 0.75 }}
                  >
                    {multiselect && (
                      <ListItemIcon
                        className={classes.searchableSelectCheckbox}
                      >
                        <Checkbox
                          edge="start"
                          checked={selectedFilterOptions.includes(
                            option.value as string
                          )}
                          disabled={option.disable}
                          disableRipple
                        />
                      </ListItemIcon>
                    )}
                    {hasAvatars && (
                      <ListItemAvatar
                        className={classes.searchableSelectListItemAvatar}
                      >
                        {CustomAvatar ? (
                          <CustomAvatar option={option} />
                        ) : (
                          <Avatar
                            alt={option.label as string}
                            src={option.avatar}
                          />
                        )}
                      </ListItemAvatar>
                    )}
                    <ListItemText
                      className={
                        option.displayIcon
                          ? classes.searchableSelectListItemTextWarning
                          : classes.searchableSelectListItemText
                      }
                      primary={
                        <>
                          {optionLabelFormatter(option.label)}
                          {` `}
                          {option.displayIcon && (
                            <Tooltip title={option.iconTooltipTitle}>
                              <FontAwesomeIcon
                                color="var(--mui-palette-Alert-warningIconColor)"
                                icon={faTriangleExclamation}
                                size="sm"
                              />
                            </Tooltip>
                          )}
                        </>
                      }
                      secondary={option.subLabel ? option.subLabel : null}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </ListItem>
          {multiselect && (
            <ListItem className={classes.searchableSelectFooter}>
              <Button variant="text" onClick={handleCancel} size="small">
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleMultiSelect}
                size="small"
              >
                Apply
              </Button>
            </ListItem>
          )}
        </List>
      </Popover>
    </Box>
  );
  const toggle = (
    <Button
      color="secondary"
      variant={selectedOptions.includes('true') ? 'contained' : 'outlined'}
      onClick={() => handleOptionToggle('true')}
    >
      {title}{' '}
      {showCounts && (
        <Chip
          size="small"
          sx={{ ml: 1 }}
          label={
            filterOptions.find((option) => option.value === 'true')?.count || 0
          }
        />
      )}
    </Button>
  );

  return (
    <div
      className={className}
      {...(isLayoutMobile &&
        titleButton === 'split-button' && { style: { width: '100%' } })}
    >
      {variant === 'dropdown' && dropdown}
      {variant === 'buttongroup' && buttonGroup}
      {variant === 'toggle' && toggle}
    </div>
  );
};

export default SearchableSelect;
